import { useCallback, useEffect, useMemo, useState } from "react";
import userJobApi from "@/api/userJob";
import { Button } from "@/components/Button/Button";
import { SmartMatchIcon } from "@/components/Icons/Icons";
import { SideBarHeader } from "@/components/Sidebar/SideBarHeader";
import { UserJobButton } from "@/components/Sidebar/UserJobs/UserJobButton";
import { UserJobsButtonGroup } from "@/components/Sidebar/UserJobs/UserJobsButtonGroup";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { openModal } from "@/store/modal/slice";
import { getActiveResumeId, getResumeJobId } from "@/store/resume/selectors";
import { getNonArchivedUserJobs } from "@/store/userJob/selectors";
import { IUserJob } from "@/types";
import { ModalTypes } from "@/types/modal";
import { trackPostHogEvent } from "@/utils/tracking";

export const SidebarUserJobs = () => {
    const dispatch = useAppDispatch();
    const resumeJobId = useAppSelector(getResumeJobId);
    const activeResumeId = useAppSelector(getActiveResumeId);
    const userJobs = useAppSelector(getNonArchivedUserJobs);

    const [localUserJobs, setLocalUserJobs] = useState<IUserJob[]>(userJobs);
    const [localActiveResumeJobId, setLocalActiveResumeJobId] = useState<
        string | null
    >(resumeJobId || null);

    const setLinkedJobId = (jobId: string) => {
        if (jobId === localActiveResumeJobId) {
            trackPostHogEvent("sidebar_jobs", {
                action_taken: "link_job",
                is_linking: false,
            });
            setLocalActiveResumeJobId(null);
        } else {
            trackPostHogEvent("sidebar_jobs", {
                action_taken: "link_job",
                is_linking: true,
            });
            setLocalActiveResumeJobId(jobId);
        }
        userJobApi.updateUserJobLinkedResumes(jobId, {
            resumeId: activeResumeId,
        });
    };

    useEffect(() => {
        setLocalUserJobs(userJobs);
    }, [userJobs]);

    const activeResumeJob = useMemo(() => {
        return localUserJobs.find(job => job.id === localActiveResumeJobId);
    }, [localUserJobs, localActiveResumeJobId]);

    const unlinkedJobs = useMemo(() => {
        return localUserJobs.filter(
            job => !job.linkedResumeIds?.includes(localActiveResumeJobId!),
        );
    }, [localUserJobs, localActiveResumeJobId]);

    const onTailorResume = useCallback(() => {
        trackPostHogEvent("sidebar_jobs", {
            action_taken: "tailor_resume_clicked",
        });
        dispatch(
            openModal({
                modalType: ModalTypes.SMART_MATCH,
                props: {
                    resumeId: activeResumeId,
                    userJobId: localActiveResumeJobId,
                },
            }),
        );
    }, [dispatch, activeResumeId, localActiveResumeJobId]);

    const onJobClick = useCallback(
        (jobId?: string) => {
            dispatch(
                openModal({
                    modalType: ModalTypes.MANAGE_USER_JOB,
                    props: {
                        userJobId: jobId,
                    },
                }),
            );
        },
        [dispatch],
    );

    return (
        <div className="relative flex h-full flex-col">
            <SideBarHeader title="Jobs" />
            <div className="mx-4 flex min-h-0 flex-1 flex-col gap-6">
                <div>
                    <AppText
                        variant="regular"
                        className="text-neutral-700"
                    >
                        Attach a job to this resume to create better results
                        with our{" "}
                        <AppText
                            as="span"
                            variant="regular"
                            className="text-nowrap !font-semibold"
                            applyGradient
                        >
                            Smart Match
                        </AppText>{" "}
                        and{" "}
                        <AppText
                            as="span"
                            variant="regular"
                            className="text-nowrap !font-semibold"
                            applyGradient
                        >
                            Smart Scribe
                        </AppText>{" "}
                        AI.
                    </AppText>
                    <div className="mt-4 flex flex-col gap-2">
                        <AppText
                            variant="contextheading"
                            className="text-neutral-700"
                        >
                            Attached Job
                        </AppText>
                        {activeResumeJob ? (
                            <UserJobButton
                                job={activeResumeJob}
                                isActive={true}
                                setLinkedJobId={() =>
                                    setLinkedJobId(activeResumeJob.id)
                                }
                                onEditJob={() => onJobClick(activeResumeJob.id)}
                                activeClassName="bg-neutral-50"
                            />
                        ) : (
                            <div className="flex h-14 flex-col items-center justify-center gap-2 rounded-lg border border-neutral-300 p-2">
                                <AppText
                                    variant="labelsbuttons"
                                    className="text-center text-neutral-500"
                                >
                                    No job attached
                                </AppText>
                            </div>
                        )}

                        <Button
                            variant="outline"
                            color="gradient"
                            leftIcon={<SmartMatchIcon gradient />}
                            disabled={!activeResumeJob}
                            onClick={onTailorResume}
                        >
                            Tailor Resume
                        </Button>
                    </div>
                </div>

                <div className="min-h-0 flex-1">
                    <UserJobsButtonGroup
                        setLinkedJobId={setLinkedJobId}
                        onCreateJob={() => onJobClick()}
                        onEditJob={onJobClick}
                        userJobs={unlinkedJobs}
                        resumeJobId={localActiveResumeJobId || ""}
                    />
                </div>
            </div>
        </div>
    );
};
